import React from 'react';

const FeatureFlagDebugPanel = React.lazy(async () => {
  const { featureFlagDebugPanel } = await import('./feature-flag-debug-panel');
  return { default: () => featureFlagDebugPanel };
});

function hasDebugToolbar() {
  return document.getElementById('hs-debug-tools') !== null;
}

// The PHP toolbar is only rendered for eligible non-production requests, so it
// gates the panel at runtime: deployed QA and staging bundles are production
// builds, where a NODE_ENV check would strip the panel entirely. Layouts print
// the toolbar after their scripts, so its presence is only final once the
// document has been parsed.
function useDebugToolbarPresent() {
  const [isPresent, setIsPresent] = React.useState(
    () => document.readyState !== 'loading' && hasDebugToolbar(),
  );

  React.useEffect(() => {
    // Parsing can finish between the first render and this effect, after which
    // DOMContentLoaded never fires again.
    if (document.readyState !== 'loading') {
      setIsPresent(hasDebugToolbar());
      return undefined;
    }

    const onLoaded = () => setIsPresent(hasDebugToolbar());
    document.addEventListener('DOMContentLoaded', onLoaded);
    return () => document.removeEventListener('DOMContentLoaded', onLoaded);
  }, []);

  return isPresent;
}

function LazyFeatureFlagDebugPanel() {
  const isToolbarPresent = useDebugToolbarPresent();

  if (!isToolbarPresent) {
    return null;
  }

  return (
    <React.Suspense fallback={null}>
      <FeatureFlagDebugPanel />
    </React.Suspense>
  );
}

export const lazyFeatureFlagDebugPanel = <LazyFeatureFlagDebugPanel />;
